// This workspace's investigations at `/runs`, newest dispatched first.
// An id from a ticket or a log opens from the command menu (#811), by the same path.
//
// **A row is a link, and that is the whole of the navigation.** The control
// plane serves `index.html` behind every path it does not claim
// (`control_plane/api.py`), so `/runs/<id>` is a page a person can paste, a
// browser can bookmark and an anchor can reach without a router. `runPagePath`
// is the same function the command menu uses and the inverse of the one `App` reads
// the id back with (`runId.ts`), so the link and the route cannot drift.

import { useState } from "react";

import { runLabel, type RunRow } from "../api/runs";
import { SOURCE_TYPES, type Setup } from "../api/sources";
import { runPagePath } from "../runId";
import { AccountName } from "./AccountName";
import { Chip, Empty, Notice, PageIntro } from "./chrome";
import { beforeFirstInvestigation, FirstRun } from "./FirstRun";
import { TicketLinks } from "./InvestigationLinks";
import type { IntegrationsStep } from "./integrationsStep";
import { runStatusLabel, runStatusTone } from "./runPresentation";
import { copyText } from "./SlackFlow";
export { FirstRun } from "./FirstRun";


/** A timestamp as the row shows it: UTC, to the second, never the reader's locale. */
export function whenLabel(iso: string | null): string {
  if (iso === null) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at
    .toISOString()
    .replace("T", " ")
    .replace(/\.\d+Z$/, "Z");
}

/**
 * How long an investigation took, from dispatch to finish, in the units a person would
 * say it in; null while it is open or when either clock is missing or unreadable.
 */
export function durationLabel(dispatched: string | null, finished: string | null): string | null {
  if (dispatched === null || finished === null) return null;
  const ms = Date.parse(finished) - Date.parse(dispatched);
  if (Number.isNaN(ms) || ms < 0) return null;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** Where the incident's first alert came in: the source's name, else the kind of sender. */
export function sourceLabel(run: Pick<RunRow, "source_name" | "source_type">): string | null {
  if (run.source_name) return run.source_name;
  if (!run.source_type) return null;
  const known = SOURCE_TYPES.find(([type]) => type === run.source_type);
  return known === undefined ? run.source_type : known[1];
}

function StatusChip({ status }: { status: string }) {
  return <Chip tone={runStatusTone(status)}>{runStatusLabel(status)}</Chip>;
}

export function RunsPage({
  runs,
  error,
  setup,
  integrationsStep,
  onReload,
  onLoadMore,
  loadingMore = false,
  hasMore = false,
}: {
  /** The rows, or null while the first read is in flight. */
  runs: RunRow[] | null;
  error: string | null;
  /** What is left before the first run, read while the list holds no real run (#422, #985); null while unread. */
  setup?: Setup | null;
  /** The optional step under the checklist (#602), or null when there is none to show. */
  integrationsStep?: IntegrationsStep | null;
  onReload: () => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  hasMore?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [incident, setIncident] = useState("all");
  const matching = (runs ?? []).filter(
    (run) =>
      (status === "all" || run.status === status) &&
      (incident === "all" || (run.incident_status === "closed" ? "resolved" : "unresolved") === incident) &&
      `${runLabel(run)} ${run.run_id}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  return (
    <>
      <PageIntro
        destination="runs"
        action={
          <button type="button" className="btn" onClick={onReload}>
            Reload
          </button>
        }
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      <section className="panel">
        <div className="investigation-tools">
          <div>
            <h2>Your investigations</h2>
          </div>
          <label className="field__label">
            Search loaded investigations
            <input
              type="search"
              className="input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Title or investigation id…"
            />
          </label>
          <label className="field__label">
            Investigation status
            <select
              className="input"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">All statuses</option>
              {[
                ...new Set([
                  "running",
                  "paused",
                  "complete",
                  "failed",
                  "budget_exhausted",
                  ...(runs ?? []).map((run) => run.status),
                ]),
              ].map((value) => (
                <option key={value} value={value}>
                  {runStatusLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label className="field__label">
            Incident status
            <select className="input" value={incident} onChange={(event) => setIncident(event.target.value)}>
              <option value="all">All incidents</option>
              <option value="unresolved">Unresolved</option>
              <option value="resolved">Resolved</option>
            </select>
          </label>
        </div>
        {runs === null && error === null ? (
          <p className="muted panel__body" role="status">
            Reading this workspace's investigations…
          </p>
        ) : null}
        {runs !== null && beforeFirstInvestigation(runs, setup ?? null) ? <FirstRun setup={setup ?? null} integrations={integrationsStep ?? null} /> : null}
        {runs !== null && runs.length > 0 && matching.length === 0 ? (
          <Empty title="No matching investigations">
            <p>Try another title or status, or load more investigations.</p>
            <button
              className="btn"
              onClick={() => {
                setQuery("");
                setStatus("all");
                setIncident("all");
              }}
            >
              Clear filters
            </button>
          </Empty>
        ) : null}
        {matching.length > 0 ? <RunTable runs={matching} /> : null}
        {runs === null ? null : (
          <div className="investigation-footer">
            <span className="muted" role="status">
              {matching.length} of {runs.length} loaded · newest dispatched first
            </span>
            {hasMore ? (
              <button className="btn" disabled={loadingMore} onClick={onLoadMore}>
                {loadingMore ? "Loading…" : "Load more investigations"}
              </button>
            ) : (
              <span className="muted">No older page available</span>
            )}
          </div>
        )}
      </section>
    </>
  );
}

function RunTable({ runs }: { runs: RunRow[] }) {
  return (
    <div className="table-wrap">
      <table className="table investigation-table">
        <thead>
          <tr>
            <th scope="col">Investigation</th>
            <th scope="col">Status</th>
            <th scope="col">Account</th>
            <th scope="col">Alert source</th>
            {/* `dispatched_at` and `finished_at` are the two clocks #230's list
                carries, and the run record is careful that dispatched is not
                started (`routes/records.py`: three clocks, three facts). The
                column is named after the fact it shows. */}
            <th scope="col">Dispatched</th>
            <th scope="col">Duration</th>
            <th scope="col">Ticket</th>
            <th scope="col">Incident</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => {
            const took = durationLabel(run.dispatched_at, run.finished_at);
            const source = sourceLabel(run);
            return (
              <tr key={run.run_id}>
                <td>
                  <span className="investigation-title">
                    <a href={runPagePath(run.run_id)} className="table__link">
                      {runLabel(run)}
                    </a>
                    <CopyId id={run.run_id} title={runLabel(run)} />
                  </span>
                  <small className="investigation-mobile-time">
                    Dispatched {whenLabel(run.dispatched_at)}
                    {took === null ? null : ` · took ${took}`}
                  </small>
                </td>
                <td>
                  <StatusChip status={run.status} />
                  {run.test ? <Chip>Test</Chip> : null}
                </td>
                <td>
                  {run.account_id ? (
                    <AccountName id={run.account_id} name={run.account_name} />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{source === null ? <span className="muted">—</span> : source}</td>
                <td>{whenLabel(run.dispatched_at)}</td>
                <td>
                  {took === null ? (
                    <span className="muted">—</span>
                  ) : (
                    <span title={`Finished ${whenLabel(run.finished_at)}`}>{took}</span>
                  )}
                </td>
                <td>
                  {(run.tickets ?? []).length === 0 ? (
                    <span className="muted">None</span>
                  ) : (
                    <TicketLinks tickets={run.tickets ?? []} />
                  )}
                </td>
                <td>{run.incident_status === "closed" ? "Resolved" : "Unresolved"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Why a copy did not happen, and where the id can be copied by hand instead. */
export const COPY_ID_REFUSED = "Not copied. The id is on the investigation's page.";

/**
 * The investigation's id, onto the clipboard: shown on the row's hover or focus, never as
 * text. What happened is said in a polite live region beside it, and a refusal says where
 * to copy the id by hand, as the connect flow's copy buttons do (#838).
 */
export function CopyId({
  id,
  title,
  clipboard,
}: {
  id: string;
  title: string;
  /** The browser's clipboard unless a test gives another. */
  clipboard?: Pick<Clipboard, "writeText">;
}) {
  const [said, setSaid] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        className="btn btn--quiet copy-id"
        title={id}
        aria-label={`Copy the id of ${title}`}
        onClick={async () => {
          const copied = await copyText(
            id,
            clipboard ?? (typeof navigator === "undefined" ? undefined : navigator.clipboard),
          );
          setSaid(copied ? "Copied" : COPY_ID_REFUSED);
        }}
        onBlur={() => setSaid(null)}
      >
        Copy id
      </button>
      <span className="copy-id__said muted" role="status" aria-live="polite">
        {said}
      </span>
    </>
  );
}
